/**
 * Which country a region is in, as far as anything in the game cares: whose street-name
 * plates go on the corners (`streetSign.ts`, DECISIONS.md §71) and whose buildings the city
 * is drawn with (`classify.ts`).
 *
 * `plain` is everywhere else, and it is a perfectly good answer: a city in none of these
 * outlines gets the generic plate and the generic palette, not somebody else's.
 */

export type Region = 'pl' | 'us' | 'fr' | 'gb' | 'de' | 'plain';

/**
 * Coarse outlines, [lat, lon], a few dozen kilometres out at worst.
 *
 * The world carries no country, and the alternative — asking Overpass which country the
 * region is in — is one more request against a server that already times out on us, for
 * a cosmetic. A border town may get its neighbour's sign. Tested first to last, so where
 * two outlines overlap the first wins; anywhere in none of them gets `plain`.
 */
const OUTLINES: { style: Region; rings: [number, number][][] }[] = [
  {
    style: 'pl',
    rings: [
      [
        [53.92, 14.25], [54.58, 16.85], [54.8, 18.3], [54.35, 19.6], [54.4, 22.8], [53.9, 23.5],
        [53.0, 23.9], [52.07, 23.6], [51.6, 23.6], [50.85, 24.15], [50.4, 24.0], [49.6, 22.7],
        [49.0, 22.6], [49.4, 21.0], [49.2, 20.0], [49.5, 19.0], [49.75, 18.6], [50.0, 17.9],
        [50.3, 16.9], [50.1, 16.8], [50.6, 16.2], [50.85, 15.0], [51.15, 15.0], [52.0, 14.7],
        [52.6, 14.1], [53.3, 14.4],
      ],
    ],
  },
  {
    style: 'fr',
    rings: [
      [
        [51.08, 2.55], [50.7, 3.2], [50.15, 4.15], [50.1, 4.85], [49.55, 5.8], [49.2, 6.85],
        [48.97, 8.2], [47.6, 7.6], [47.4, 7.0], [46.4, 6.15], [46.0, 6.9], [45.1, 6.6],
        [44.15, 7.65], [43.78, 7.5], [43.1, 6.0], [43.3, 5.3], [43.4, 3.5], [42.43, 3.15],
        [42.7, 1.7], [42.8, 0.0], [43.35, -1.78], [44.6, -1.25], [46.2, -1.5], [47.3, -2.6],
        [47.8, -4.4], [48.4, -4.8], [48.85, -3.0], [48.65, -1.5], [49.7, -1.9], [49.5, 0.1],
        [50.0, 1.5], [50.9, 1.6],
      ],
    ],
  },
  {
    style: 'de',
    rings: [
      [
        [54.9, 8.3], [54.8, 9.9], [54.45, 11.0], [54.6, 13.6], [53.92, 14.25], [53.3, 14.4],
        [52.6, 14.1], [52.0, 14.7], [51.15, 15.0], [50.85, 14.8], [50.7, 13.0], [50.2, 12.1],
        [49.3, 12.9], [48.75, 13.8], [48.57, 13.45], [47.6, 13.0], [47.45, 10.9], [47.55, 9.6],
        [47.6, 7.6], [48.97, 8.2], [49.2, 6.6], [49.5, 6.4], [50.1, 6.1], [50.75, 6.0],
        [51.85, 5.95], [51.9, 6.7], [52.4, 7.05], [53.3, 7.2], [53.6, 6.9],
      ],
    ],
  },
  {
    style: 'gb',
    rings: [
      // Great Britain
      [
        [50.05, -5.7], [50.2, -3.6], [50.6, -2.5], [50.7, -1.0], [50.75, 0.3], [51.1, 1.45],
        [51.5, 1.5], [52.5, 1.8], [53.0, 1.4], [53.6, 0.2], [54.6, -0.8], [55.8, -1.9],
        [56.4, -2.4], [57.7, -1.7], [58.7, -3.0], [58.6, -5.0], [57.6, -6.6], [56.3, -6.5],
        [55.3, -5.8], [54.6, -5.0], [54.9, -3.4], [54.1, -3.3], [53.4, -3.2], [53.4, -4.7],
        [52.8, -4.8], [52.0, -5.4], [51.6, -5.3], [51.5, -3.2], [51.2, -4.6],
      ],
      // Northern Ireland
      [[55.3, -6.0], [55.2, -7.4], [54.6, -8.2], [54.1, -7.0], [54.0, -6.1], [54.4, -5.4]],
    ],
  },
  {
    style: 'us',
    rings: [
      // The lower 48. The Great Lakes are traced through rather than boxed over, or Toronto
      // and Montréal would get green signs.
      [
        [49.0, -124.8], [49.0, -95.2], [48.3, -89.5], [46.5, -84.5], [45.3, -82.5], [43.0, -82.4],
        [42.05, -83.15], [41.7, -82.7], [42.9, -78.9], [43.26, -79.06], [43.6, -77.5],
        [44.1, -76.4], [45.0, -74.7], [45.0, -71.5], [47.4, -69.2], [47.0, -67.8], [44.8, -66.9],
        [42.0, -69.9], [41.0, -71.8], [40.4, -73.9], [39.0, -74.8], [36.9, -75.9], [35.2, -75.5],
        [32.0, -80.8], [30.4, -81.3], [25.6, -80.05], [25.1, -80.3], [24.5, -81.8], [26.0, -82.2], [29.5, -85.2],
        [30.2, -88.5], [29.0, -89.4], [29.5, -93.5], [26.0, -97.2], [29.8, -101.5], [29.0, -103.0],
        [31.8, -106.5], [31.3, -108.2], [31.3, -111.1], [32.5, -114.8], [32.5, -117.1],
        [34.4, -120.6], [37.8, -122.6], [40.4, -124.4], [42.0, -124.5], [46.2, -124.1],
        [48.4, -124.8],
      ],
    ],
  },
];

function inRing(ring: [number, number][], lat: number, lon: number): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [ay, ax] = ring[i];
    const [by, bx] = ring[j];
    if (ay > lat !== by > lat && lon < ((bx - ax) * (lat - ay)) / (by - ay) + ax) inside = !inside;
  }
  return inside;
}

export function regionAt(lat: number, lon: number): Region {
  for (const { style, rings } of OUTLINES) {
    if (rings.some((ring) => inRing(ring, lat, lon))) return style;
  }
  return 'plain';
}
